import {
    AutomationRule,
    AutomationWorker,
    AutomationWorkerId,
    CatalogUsage,
    ConditionKind,
    PaymentMatchValue,
    ReconsiderationDocument,
    RuleActionType,
    RuleMatch,
    UsageBadgePresentation,
    WorkerDefaultScope,
} from './worker-automation.types';
import { normalizePayerAdjustmentGroup } from '../../dictionary/payer-adjustment-groups';
import {
    createEmptyMatch,
    formatRuleMatchSummary,
    getAvailableMatchScopes,
    getConditionKindsForScope,
    getConditionValues,
} from './worker-automation-scope.registry';

export {
    conditionMetaForScope as conditionMeta,
    createEmptyMatch,
    formatMatchBlockSummary,
    formatRuleMatchSummary,
    getAvailableMatchScopes,
    getConditionKindsForScope as getConditionKinds,
    getConditionValues,
    getScopeLabel,
} from './worker-automation-scope.registry';

export interface ActionMeta {
    bucket: string;
    label: string;
}

export const RULE_ACTION_SELECT_LABELS: Record<RuleActionType, string> = {
    CLOSE_OUT: 'Close Out',
    SWITCH_TO_PRIVATE_PAY: 'Convert to Private Pay',
    CREATE_APPEAL: 'Reconsideration',
    CREATE_MESSAGE: 'Create Task',
};

/** Rule actions available for a given automation worker. Reconsideration is denial-only. */
export function getAvailableRuleActionTypes(workerId: AutomationWorkerId): RuleActionType[] {
    const actions: RuleActionType[] = ['CLOSE_OUT', 'SWITCH_TO_PRIVATE_PAY'];

    if (workerId === 'denial') {
        actions.push('CREATE_APPEAL');
    }

    actions.push('CREATE_MESSAGE');

    return actions;
}

export function getActionMeta(type: string): ActionMeta {
    switch (type) {
        case 'CLOSE_OUT':
            return { label: 'Close Out', bucket: 'Bucket 1' };
        case 'SWITCH_TO_PRIVATE_PAY':
            return { label: 'Convert to Private Pay', bucket: 'Bucket 2' };
        case 'CREATE_APPEAL':
            return { label: 'Reconsideration', bucket: 'Bucket 3' };
        case 'CREATE_MESSAGE':
            return { label: 'Create Task', bucket: 'Bucket 4' };
        default:
            return { label: type, bucket: '' };
    }
}

export function getDefaultActionValue(type: string): Record<string, unknown> {
    switch (type) {
        case 'CLOSE_OUT':
            return { closeoutDescription: '' };
        case 'SWITCH_TO_PRIVATE_PAY':
            return { memo: '' };
        case 'CREATE_APPEAL':
            return { documents: [], prompt: '' };
        case 'CREATE_MESSAGE':
            return {
                assigned: [],
                messageCategory: 'PAYER_FOLLOWUP',
                messageOwner: 'ENTER',
                suggestedResolution: '',
            };
        default:
            return {};
    }
}

export const ENTER_TASK_FEE_DISCLAIMER = 'Additional fees may apply.';

export function isEnterTaskMessageOwner(messageOwner: unknown): boolean {
    return messageOwner !== 'PROVIDER';
}

export function stripEnterTaskFeeDisclaimer(text: string): string {
    const trimmed = text.trimEnd();

    if (trimmed.endsWith(ENTER_TASK_FEE_DISCLAIMER)) {
        return trimmed.slice(0, -ENTER_TASK_FEE_DISCLAIMER.length).replace(/\s+$/, '');
    }

    return trimmed;
}

export function buildCreateMessageFirstComment(suggestedResolution: string, messageOwner: unknown): string {
    const body = stripEnterTaskFeeDisclaimer(suggestedResolution).trim();

    if (!isEnterTaskMessageOwner(messageOwner)) {
        return body;
    }

    if (!body) {
        return ENTER_TASK_FEE_DISCLAIMER;
    }

    return `${body}\n\n${ENTER_TASK_FEE_DISCLAIMER}`;
}

function extractReconsiderationDocumentIds(value: Record<string, unknown>): string[] {
    const documentIds = value.documentIds;
    if (Array.isArray(documentIds)) {
        return documentIds
            .map(id => (typeof id === 'string' ? id.trim() : ''))
            .filter(Boolean);
    }

    const documents = value.documents;
    if (!Array.isArray(documents)) {
        return [];
    }

    return documents
        .map(doc => {
            if (typeof doc === 'string') {
                return doc.trim();
            }

            if (doc && typeof doc === 'object' && 'documentId' in doc) {
                return String((doc as ReconsiderationDocument).documentId).trim();
            }

            return '';
        })
        .filter(Boolean);
}

function extractReconsiderationDocuments(value: Record<string, unknown>): ReconsiderationDocument[] {
    const documents = value.documents;
    if (Array.isArray(documents) && documents.length > 0) {
        return documents
            .map(doc => {
                if (typeof doc === 'string') {
                    const documentId = doc.trim();
                    return documentId ? { documentId, name: documentId } : null;
                }

                if (doc && typeof doc === 'object' && 'documentId' in doc) {
                    const documentId = String((doc as ReconsiderationDocument).documentId).trim();
                    if (!documentId) {
                        return null;
                    }

                    const name = String((doc as ReconsiderationDocument).name ?? '').trim();
                    return { documentId, name: name || documentId };
                }

                return null;
            })
            .filter((doc): doc is ReconsiderationDocument => doc !== null);
    }

    return extractReconsiderationDocumentIds(value).map(documentId => ({
        documentId,
        name: documentId,
    }));
}

function isCreateAppealAction(type: string): boolean {
    // Legacy configs may still store CREATE_RECONSIDERATION; treat as CREATE_APPEAL.
    return type === 'CREATE_APPEAL' || type === 'CREATE_RECONSIDERATION';
}

const KNOWN_RULE_ACTION_TYPES: ReadonlySet<string> = new Set([
    'CLOSE_OUT',
    'SWITCH_TO_PRIVATE_PAY',
    'CREATE_APPEAL',
    'CREATE_MESSAGE',
]);

/** Maps legacy / unknown API action types onto the UI RuleActionType set. */
export function resolveRuleActionType(type: string | null | undefined): RuleActionType | null {
    if (!type) {
        return null;
    }

    if (isCreateAppealAction(type)) {
        return 'CREATE_APPEAL';
    }

    if (KNOWN_RULE_ACTION_TYPES.has(type)) {
        return type as RuleActionType;
    }

    return null;
}

export function normalizeReconsiderationRuleForEdit(rule: AutomationRule): AutomationRule {
    if (!isCreateAppealAction(rule.action.type)) {
        return rule;
    }

    const value = rule.action.value as Record<string, unknown>;
    const { documentIds: _documentIds, documents: _documents, ...rest } = value;

    return {
        ...rule,
        action: {
            ...rule.action,
            type: 'CREATE_APPEAL',
            value: {
                ...rest,
                documents: extractReconsiderationDocuments(value),
            },
        },
    };
}

export function normalizeReconsiderationRuleForPersist(rule: AutomationRule): AutomationRule {
    if (!isCreateAppealAction(rule.action.type)) {
        return rule;
    }

    const value = rule.action.value as Record<string, unknown>;
    const { documentIds: _documentIds, documents: _documents, ...rest } = value;

    return {
        ...rule,
        action: {
            ...rule.action,
            type: 'CREATE_APPEAL',
            value: {
                ...rest,
                documentIds: extractReconsiderationDocumentIds(value),
            },
        },
    };
}

export function normalizeCreateMessageRuleForEdit(rule: AutomationRule): AutomationRule {
    if (rule.action.type !== 'CREATE_MESSAGE') {
        return rule;
    }

    const value = rule.action.value as Record<string, unknown>;
    const suggestedResolution = value.suggestedResolution;

    if (typeof suggestedResolution !== 'string' || !suggestedResolution) {
        return rule;
    }

    return {
        ...rule,
        action: {
            ...rule.action,
            value: {
                ...value,
                suggestedResolution: stripEnterTaskFeeDisclaimer(suggestedResolution),
            },
        },
    };
}

export function normalizeCreateMessageRuleForPersist(rule: AutomationRule): AutomationRule {
    if (rule.action.type !== 'CREATE_MESSAGE') {
        return rule;
    }

    const value = rule.action.value as Record<string, unknown>;

    return {
        ...rule,
        action: {
            ...rule.action,
            value: {
                ...value,
                suggestedResolution: buildCreateMessageFirstComment(
                    String(value.suggestedResolution ?? ''),
                    value.messageOwner,
                ),
            },
        },
    };
}

function normalizePaymentMatchCarcGroups(match: RuleMatch): RuleMatch {
    if (match.scope !== 'PAYMENT' || !('carc' in match.value)) {
        return match;
    }

    const payment = match.value as PaymentMatchValue;

    return {
        ...match,
        value: {
            ...payment,
            carc: {
                ...payment.carc,
                group: payment.carc.group.map(group => normalizePayerAdjustmentGroup(group)),
            },
        },
    };
}

function normalizeAutomationRuleMatchValues(rule: AutomationRule): AutomationRule {
    return {
        ...rule,
        match: rule.match.map(normalizePaymentMatchCarcGroups),
    };
}

function normalizeRuleActionType(rule: AutomationRule): AutomationRule {
    const resolved = resolveRuleActionType(rule.action?.type);
    if (!resolved || resolved === rule.action.type) {
        return rule;
    }

    return {
        ...rule,
        action: {
            ...rule.action,
            type: resolved,
            value: rule.action?.value ?? getDefaultActionValue(resolved),
        },
    };
}

export function normalizeAutomationRuleForEdit(rule: AutomationRule): AutomationRule {
    return normalizeAutomationRuleMatchValues(
        normalizeReconsiderationRuleForEdit(
            normalizeCreateMessageRuleForEdit(normalizeRuleActionType(rule)),
        ),
    );
}

export function normalizeAutomationRuleForPersist(rule: AutomationRule): AutomationRule {
    return normalizeAutomationRuleMatchValues(
        normalizeReconsiderationRuleForPersist(normalizeCreateMessageRuleForPersist(rule)),
    );
}

export function createDefaultMatch(workerScope: WorkerDefaultScope) {
    return [createEmptyMatch(workerScope)];
}

function createRuleId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `rule-${crypto.randomUUID()}`;
    }

    return `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createEmptyRule(workerScope: WorkerDefaultScope): AutomationRule {
    return {
        id: createRuleId(),
        name: 'New rule',
        match: createDefaultMatch(workerScope),
        action: { type: 'CLOSE_OUT', value: getDefaultActionValue('CLOSE_OUT') },
    };
}

/** @deprecated Use formatRuleMatchSummary(rule.match) */
export function formatMatchSummary(rule: AutomationRule, _workerScope: WorkerDefaultScope): string {
    return formatRuleMatchSummary(rule.match);
}

export function getWorkerById(
    workers: Record<AutomationWorkerId, AutomationWorker>,
    id: AutomationWorkerId,
): AutomationWorker {
    return workers[id];
}

export function collectCatalogValuesFromMatch(
    match: AutomationRule['match'][number],
): Array<{ kind: ConditionKind; value: string }> {
    return getConditionKindsForScope(match.scope).flatMap((kind) =>
        getConditionValues(match, kind).map((value) => ({ kind, value })),
    );
}

export function getCatalogValueUsage(
    worker: AutomationWorker,
    excludeRuleId: string,
    kind: ConditionKind,
    value: string,
): CatalogUsage {
    const ruleNames: string[] = [];
    const ruleNumbers: number[] = [];

    worker.rules.forEach((rule, index) => {
        if (rule.id === excludeRuleId) {
            return;
        }

        const used = rule.match.some(
            match =>
                getConditionKindsForScope(match.scope).includes(kind) &&
                getConditionValues(match, kind).some(entry => String(entry) === String(value)),
        );

        if (used) {
            ruleNames.push(rule.name);
            ruleNumbers.push(index + 1);
        }
    });

    return { count: ruleNames.length, ruleNames, ruleNumbers };
}

export function getUsageBadgePresentation(usage: CatalogUsage): UsageBadgePresentation {
    if (usage.count === 0) {
        return {
            used: false,
            notUsed: true,
            label: 'Not yet used',
            short: '',
            title: '',
        };
    }

    const uniq = [...new Set(usage.ruleNumbers)].sort((left, right) => left - right);
    const short = `#${uniq.join(', #')}`;
    const label = uniq.length === 1 ? `Used in rule ${short}` : `Used in rules ${short}`;

    return {
        used: true,
        notUsed: false,
        label,
        short,
        title: label,
    };
}

/** @deprecated Use getUsageBadgePresentation(usage).label */
export function formatUsageLabel(usage: CatalogUsage): string {
    return getUsageBadgePresentation(usage).label;
}
